/**
 * Products Module
 *
 * Handles product-specific functionality for index, new, and edit pages.
 * Auto-initializes based on page context.
 *
 * Usage in Twig:
 * Index: <div data-controller="products-index" data-delete-url="/products" data-bulk-url="/products/bulk">
 * New: <form id="product-form" data-controller="products-form">
 * Edit: <form id="menu-item-form" data-controller="products-form" data-product-id="123">
 */

import { $, $$, on, ready } from '../utils/dom.js';
import { getToken, getHeaders } from '../utils/csrf.js';
import { showNotification } from './notifications.js';

// ============================================================================
// Product Index Page
// ============================================================================

/**
 * Delete a product via AJAX
 * @param {string} id - Product ID
 * @param {string} name - Product name (for confirmation)
 */
export function deleteProduct(id, name) {
    const confirmMsg = name
        ? `Are you sure you want to delete the product "${name}"?\n\nThis action cannot be undone.`
        : 'Are you sure you want to delete this product?\n\nThis action cannot be undone.';

    if (!confirm(confirmMsg)) {
        return;
    }

    const formData = new FormData();
    formData.append('action', 'delete_product');
    formData.append('product_id', id);

    fetch('/admin-ajax', {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
    })
    .then(response => response.json())
    .then(data => {
        if (data.success) {
            window.location.href = '/products';
        } else {
            showNotification(data.message || 'Failed to delete product', 'error');
        }
    })
    .catch(error => {
        console.error('Error:', error);
        showNotification('An error occurred while deleting the product', 'error');
    });
}

/**
 * Execute bulk action on products
 * @param {string} action - Action to perform (available, unavailable, delete)
 * @param {string[]} ids - Array of product IDs
 * @param {HTMLElement} [button] - Optional button element to show loading state
 */
export function executeBulkAction(action, ids, button = null) {
    const originalHtml = button?.innerHTML;

    if (button) {
        button.disabled = true;
        button.innerHTML = '<svg class="animate-spin h-4 w-4 mr-2 inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg> Processing...';
    }

    fetch('/products/bulk', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
            _token: getToken(),
            action: action,
            ids: ids
        })
    })
    .then(response => {
        if (!response.ok) {
            return response.json().then(err => {
                throw new Error(err.message || 'Failed to perform bulk action');
            });
        }
        return response.json();
    })
    .then(data => {
        if (data.success) {
            showNotification(data.message || 'Bulk action completed successfully', 'success');
            setTimeout(() => window.location.reload(), 1000);
        } else {
            throw new Error(data.message || 'Bulk action failed');
        }
    })
    .catch(error => {
        console.error('Bulk action error:', error);
        showNotification(error.message || 'An error occurred while performing the bulk action', 'error');
        if (button) {
            button.disabled = false;
            button.innerHTML = originalHtml;
        }
    });
}

/**
 * Initialize products index page functionality
 * @param {HTMLElement} container - The container element
 */
function initProductsIndex(container) {
    const selectAllCheckbox = $('#select-all', container);
    const bulkActionSelect = $('#bulk-action-select', container);
    const applyBulkActionBtn = $('#apply-bulk-action', container);

    function getRowCheckboxes() {
        return $$('.row-checkbox', container);
    }

    function updateSelectAllState() {
        if (!selectAllCheckbox) return;

        const checkboxes = getRowCheckboxes();
        const checkedCount = checkboxes.filter(cb => cb.checked).length;

        selectAllCheckbox.checked = checkboxes.length > 0 && checkedCount === checkboxes.length;
        selectAllCheckbox.indeterminate = checkedCount > 0 && checkedCount < checkboxes.length;
    }

    function updateBulkActionButton() {
        const checkedCount = getRowCheckboxes().filter(cb => cb.checked).length;
        if (applyBulkActionBtn) {
            applyBulkActionBtn.disabled = checkedCount === 0;
            applyBulkActionBtn.classList.toggle('opacity-50', checkedCount === 0);
            applyBulkActionBtn.classList.toggle('cursor-not-allowed', checkedCount === 0);
        }
    }

    // Select/Deselect All
    if (selectAllCheckbox) {
        selectAllCheckbox.addEventListener('change', function() {
            getRowCheckboxes().forEach(cb => cb.checked = this.checked);
            updateBulkActionButton();
        });
    }

    // Update states when individual checkboxes change
    on(container, 'change', '.row-checkbox', () => {
        updateSelectAllState();
        updateBulkActionButton();
    });

    // Apply Bulk Action
    if (applyBulkActionBtn) {
        applyBulkActionBtn.addEventListener('click', function() {
            const selectedAction = bulkActionSelect?.value || '';
            const checkedCheckboxes = getRowCheckboxes().filter(cb => cb.checked);

            if (!selectedAction) {
                showNotification('Please select an action', 'error');
                return;
            }

            if (checkedCheckboxes.length === 0) {
                showNotification('Please select at least one product', 'error');
                return;
            }

            const selectedIds = checkedCheckboxes.map(cb => cb.value);
            const selectedNames = checkedCheckboxes.map(cb => cb.getAttribute('data-name'));

            const actionText = {
                'available': 'mark as available',
                'unavailable': 'mark as unavailable',
                'delete': 'delete'
            }[selectedAction];

            const confirmMessage = `Are you sure you want to ${actionText} ${selectedIds.length} product${selectedIds.length === 1 ? '' : 's'}?`;

            if (selectedAction === 'delete') {
                const productNames = selectedNames.join(', ');
                if (!confirm(`${confirmMessage}\n\nProducts: ${productNames}\n\nThis action cannot be undone.`)) {
                    return;
                }
            } else if (!confirm(confirmMessage)) {
                return;
            }

            executeBulkAction(selectedAction, selectedIds, this);
        });
    }

    // Initial state
    updateBulkActionButton();
}

// ============================================================================
// Product Form (New/Edit)
// ============================================================================

/**
 * Initialize product form functionality
 * @param {HTMLFormElement} form - The form element
 */
function initProductForm(form) {
    const productId = form.dataset.productId;
    const isEdit = !!productId;

    // Form validation
    form.addEventListener('submit', function(e) {
        const nameInput = $('#name', form);
        const priceInput = $('#price', form);
        const name = nameInput?.value.trim();
        const price = priceInput?.value;

        if (!name) {
            alert('Please enter a product name');
            nameInput?.focus();
            e.preventDefault();
            return;
        }

        if (!price || parseFloat(price) < 0) {
            alert('Please enter a valid price');
            priceInput?.focus();
            e.preventDefault();
            return;
        }
    });

    // Inventory toggle (new product form)
    const trackInventoryCheckbox = $('#track_inventory', form);
    const inventoryFields = $('#inventory-fields', form);

    if (trackInventoryCheckbox && inventoryFields) {
        trackInventoryCheckbox.addEventListener('change', function() {
            inventoryFields.classList.toggle('hidden', !this.checked);
        });
    }

    // Edit-specific functionality
    if (isEdit) {
        initEditFeatures(form, productId);
    }

    // Image URL preview
    const imageUrlInput = $('#image_url', form);
    if (imageUrlInput) {
        imageUrlInput.addEventListener('input', function() {
            const url = this.value;
            if (url) {
                try {
                    new URL(url);
                    // Valid URL - could preview image here
                } catch {
                    // Invalid URL
                }
            }
        });
    }
}

/**
 * Initialize edit-specific features
 * @param {HTMLFormElement} form - The form element
 * @param {string} productId - The product ID
 */
function initEditFeatures(form, productId) {
    // Auto-save functionality
    let autoSaveTimeout;
    let hasUnsavedChanges = false;

    function autoSave() {
        hasUnsavedChanges = true;
        clearTimeout(autoSaveTimeout);

        autoSaveTimeout = setTimeout(() => {
            if (hasUnsavedChanges) {
                console.log('Auto-saving...');
                // In a full implementation, make AJAX call here
                hasUnsavedChanges = false;
            }
        }, 2000);
    }

    // Listen for changes on form inputs
    $$('input, textarea, select', form).forEach(input => {
        input.addEventListener('input', autoSave);
        input.addEventListener('change', autoSave);
    });

    // Mark form as saved when submitted
    form.addEventListener('submit', () => {
        hasUnsavedChanges = false;
    });

    // Warn about unsaved changes
    window.addEventListener('beforeunload', function(e) {
        if (hasUnsavedChanges) {
            e.preventDefault();
            e.returnValue = '';
            return '';
        }
    });

    // Keyboard shortcuts (Ctrl/Cmd + S to save)
    document.addEventListener('keydown', function(e) {
        if ((e.ctrlKey || e.metaKey) && e.key === 's') {
            e.preventDefault();
            form.submit();
        }
    });
}

/**
 * Duplicate a product
 * @param {string} productId - The product ID to duplicate
 */
export function duplicateProduct(productId) {
    if (confirm('Create a duplicate of this product?')) {
        window.location.href = `/products/new?duplicate=${productId}`;
    }
}

/**
 * Show more actions dropdown
 */
export function showMoreActions() {
    const dropdown = $('#more-actions-dropdown');
    if (dropdown) {
        dropdown.classList.remove('hidden');
    }
}

/**
 * Hide more actions dropdown
 */
export function hideMoreActions() {
    const dropdown = $('#more-actions-dropdown');
    if (dropdown) {
        dropdown.classList.add('hidden');
    }
}

// ============================================================================
// Auto-initialization
// ============================================================================

ready(() => {
    // Initialize products index pages
    $$('[data-controller="products-index"]').forEach(initProductsIndex);

    // Initialize product forms
    $$('[data-controller="products-form"], #product-form, #menu-item-form').forEach(form => {
        if (form.tagName === 'FORM') {
            initProductForm(form);
        }
    });

    // Set up click handler for more actions dropdown backdrop
    const dropdown = $('#more-actions-dropdown');
    if (dropdown) {
        const backdrop = dropdown.querySelector('.absolute.inset-0');
        if (backdrop) {
            backdrop.addEventListener('click', hideMoreActions);
        }
    }
});

// ============================================================================
// Expose globally for legacy onclick handlers
// ============================================================================

window.deleteProduct = deleteProduct;
// Don't expose executeBulkAction globally — it's hardcoded to /products/bulk
// and overwrites page-specific inline functions (e.g., modifier groups bulk action)
window.duplicateProduct = duplicateProduct;
window.showMoreActions = showMoreActions;
window.hideMoreActions = hideMoreActions;

export default {
    deleteProduct,
    executeBulkAction,
    duplicateProduct,
    showMoreActions,
    hideMoreActions
};
